import React, { useCallback, useEffect, useState } from "react";
import { Nav, Dropdown, Button } from "react-bootstrap";
import LocationControlDropdown from "./locationControlDropdown";
import ProfileDropdown from "components/navbar/top/profileDropdown";
import LanguageBtn from "components/navbar/top/languageBtn";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "providers/appProvider";
import SubscriptionStatus from "./subscriptionStatus";
import { USER_ROLES } from "config/roles.config";
import RetakeTourButton from 'components/onboarding/RetakeTourButton';
import useSocket from 'hooks/useSocket';
import { playNotificationSound, preloadNotificationSound } from 'utils/notificationSound';
import { requestNotificationPermissionOnInteraction, showDesktopNotification } from 'utils/pushNotification';
import { claimNotification } from 'utils/tabCoordinator';
import { getBrandConfig } from 'config/brandConfig';
import { useTranslation } from "react-i18next";
import moment from "moment";

const TopNavRightSideNavItem = () => {
  const [isAppBeheerder, setIsAppBeheerder] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    isAttendanceEnabled,
    notificationsCount,
    setNotificationsCount,
    attendancePhase,
    attendanceBusy,
    breakTaken,
    clockInTime,
    workSeconds,
    breakSeconds,
    handleBreak,
    handleResume,
    handleClockOut,
    formatSecondsToHMS
  } = useAppContext();
  const isClientUser = localStorage.getItem("UserRole") === USER_ROLES.CLIENT;

  useEffect(() => {
    preloadNotificationSound();
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    const userId = localStorage.getItem("UserId");
    if (!userId) return;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/notificationsRoute/notifications/user/${userId}/unread-count`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      if (typeof data?.count === "number") setNotificationsCount(data.count);
    } catch (error) {
      console.error("Error fetching unread notification count:", error);
    }
  }, [setNotificationsCount]);

  useEffect(() => {
    refreshUnreadCount();

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    };
    window.addEventListener("online", refreshUnreadCount);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", refreshUnreadCount);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshUnreadCount]);

  useEffect(() => {
    const fetchUserRole = async () => {
      const userId = localStorage.getItem("UserId");
      if (!userId) {
        console.error("User ID not found in session storage.");
        return;
      }

      try {
        const rolesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        const rolesMap = rolesResponse.data.reduce((map, role) => {
          map[role.RoleId] = role.Name.toLowerCase();
          return map;
        }, {});

        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        const userRole = rolesMap[userResponse.data.Role]?.toLowerCase();
        setIsAppBeheerder(userRole === USER_ROLES.APP_OWNER.toLowerCase());
      } catch (error) {
        console.error("Error fetching user role:", error);
        setIsAppBeheerder(false);
      }
    };

    fetchUserRole();
  }, []);

  useSocket('connect', refreshUnreadCount);

  useSocket('notification:count', (payload) => {
    if (typeof payload?.unreadCount === "number") setNotificationsCount(payload.unreadCount);
  });

  useSocket('notification:new', async (payload) => {
    if (typeof payload?.unreadCount === "number") {
      setNotificationsCount(payload.unreadCount);
    } else {
      refreshUnreadCount();
    }

    if (!payload?.pushAlert) return;

    requestNotificationPermissionOnInteraction();

    if (!(await claimNotification(payload?.NotificationId))) return;

    playNotificationSound();
    const appName = getBrandConfig().organizationName;
    showDesktopNotification(payload?.RaisedFromName ? `${payload.RaisedFromName} · ${appName}` : appName, {
      body: payload?.Message,
      tag: payload?.NotificationId,
      onClick: () => navigate("/notifications"),
    });
  });

  const onNotificationClick = () => {
    navigate("/notifications"); 
  };

  return (
    <Nav
      navbar
      className="navbar-nav-icons ms-auto flex-row align-items-center gap-3"
      as="ul"
    >
      <div className="d-none d-md-block">
        <SubscriptionStatus />
      </div>
      <LanguageBtn />

    {isAttendanceEnabled && (
      <div id="header-attendance-slot" className="d-inline-flex align-items-center">
        {(attendancePhase === "working" || attendancePhase === "break") && (
          <Dropdown align="end" className="d-inline-block">
            <Dropdown.Toggle
              as="button"
              className={`btn btn-sm ${
              attendancePhase === "break" ? "bg-warning-subtle" : "bg-primary-subtle"
            } border rounded-pill shadow-none d-inline-flex align-items-center gap-2 px-3 py-2 cursor-pointer dropdown-toggle-none text-decoration-none`}
            >
              <svg width="9" height="9" viewBox="0 0 9 9" className="flex-shrink-0">
                <circle
                  cx="4.5"
                  cy="4.5"
                  r="4.5"
                  fill={attendancePhase === "working" ? "#16a34a" : "#f59e0b"}
                />
              </svg>

              <span className="text-dark fw-normal font-size-11 lh-1 text-capitalize">
                {attendancePhase === "working" ? t("working") : t("onBreak")}
              </span>

              <span className="text-400 font-size-11 lh-1">&middot;</span>

              <span className="text-dark fw-medium font-size-11 lh-1 attendance-timer-digits">
                {formatSecondsToHMS(attendancePhase === "working" ? workSeconds : breakSeconds)}
              </span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="attendance-menu shadow-sm border rounded-3 p-3 mt-2 dropdown-menu-end">
              <div className="px-2 py-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <svg width="8" height="8" viewBox="0 0 8 8" className="flex-shrink-0">
                    <circle
                      cx="4"
                      cy="4"
                      r="4"
                      fill={attendancePhase === "working" ? "#16a34a" : "#f59e0b"}
                    />
                  </svg>
                  <span className="data-font-colour data-font-size text-capitalize fw-normal">
                    {attendancePhase === "working" ? t("working") : t("onBreak")}
                  </span>
                </div>

                <h5 className="mb-1">
                  {formatSecondsToHMS(attendancePhase === "working" ? workSeconds : breakSeconds)}
                </h5>

                <div className="data-font-colour fs-10 mb-2 text-nowrap fw-normal">
                 {t("since")} {clockInTime || moment(process.env.REACT_APP_DEFAULT_WORK_START || "09:00:00", "HH:mm:ss").format("HH:mm")} &middot; {moment(process.env.REACT_APP_DEFAULT_WORK_START || "09:00:00", "HH:mm:ss").format("HH:mm")} &ndash; {moment(process.env.REACT_APP_DEFAULT_WORK_END || "17:00:00", "HH:mm:ss").format("HH:mm")}
                </div>

                <div className="d-flex align-items-center gap-2">
                  {attendancePhase === "working" && breakTaken ? null : attendancePhase === "working" ? (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="fw-semibold px-3 py-1 rounded-pill shadow-sm text-nowrap d-flex align-items-center justify-content-center gap-2 flex-grow-1"
                      onClick={handleBreak}
                      disabled={attendanceBusy}
                    >
                      <span>{t("break")}</span>
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      className="rounded-pill px-3 py-1 fw-semibold shadow-none text-nowrap d-flex align-items-center justify-content-center gap-2 flex-grow-1"
                      onClick={handleResume}
                      disabled={attendanceBusy}
                    >
                     <span>{t("resume", "Resume")}</span>
                    </Button>
                  )}

                  <Button
                    variant="danger"
                    size="sm"
                    className="rounded-pill px-3 py-1 fw-semibold text-white shadow-none text-nowrap d-flex align-items-center justify-content-center gap-2 flex-grow-1"
                    onClick={handleClockOut}
                    disabled={attendanceBusy}
                  >
                    <span>{t("clockOut")}</span>
                  </Button>
                </div>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      )}
      <RetakeTourButton />

      {/* Notification Icon */}
        <div
          className="d-none d-md-block position-relative"
          style={{ cursor: "pointer" }}
          onClick={onNotificationClick}
        >
          <FontAwesomeIcon icon={faBell} size="lg" />
          {notificationsCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning 
              d-flex align-items-center justify-content-center notification-badge lh-1">
              {notificationsCount > 99 ? "99+" : notificationsCount}
            </span>
          )}
        </div>

      {/* Location dropdown */}
      <LocationControlDropdown />

      {/* Profile dropdown */}
      <ProfileDropdown />
    </Nav>
  );
};

TopNavRightSideNavItem.propTypes = {
  notifications: PropTypes.array,
  onNotificationClick: PropTypes.func,
};

export default TopNavRightSideNavItem;